import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import Footer from "../Footer/Footer";
import GLOBALS from "../../utils/Globals";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { getUserProfileAction } from "../../redux/slices/users/usersSlice";
import { Modal, Button, Alert, Spinner } from "react-bootstrap";
import { FaHeadset, FaEnvelope, FaPaperPlane } from "react-icons/fa";
import { FaTriangleExclamation, FaWifi } from "react-icons/fa6";
import { MdRefresh } from "react-icons/md";

function Support() {
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getUserProfileAction());
  }, [dispatch]);
  const { error: profileError, loading: profileLoading, profile } = useSelector((state) => state?.users);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmittedData({
      subject: e.target[0].value,
      message: e.target[1].value
    });
    setShowModal(true);
    
    toast.info("Please confirm your submission", {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const handleConfirm = useCallback(async () => {
    try {
      setSubmissionLoading(true);
      setSubmissionError(null);

      const formData = new FormData();
      formData.append("summary", submittedData.subject);
      formData.append("message", submittedData.message);
      formData.append("org", profile.user.org);
      formData.append("fullname", profile.user.fullname);
      formData.append("email", profile.user.email);
      formData.append("account_id", profile.user.account_id);

      toast.info("Submitting support ticket...", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      // Debug environment detection
      console.log('🔍 Support Environment Check:', {
        REACT_APP_ENV: process.env.REACT_APP_ENV,
        NODE_ENV: process.env.NODE_ENV,
        hostname: window.location.hostname,
        href: window.location.href
      });

      // Use production URL if we're on the production domain or if REACT_APP_ENV is PROD
      const isProduction = process.env.REACT_APP_ENV === "PROD" ||
                           window.location.hostname === "synchcor-nutrad.com" ||
                           window.location.hostname.includes("synchcor-nutrad.com");

      const baseURLJIRA = isProduction
        ? GLOBALS.PROD.baseURLJIRA
        : GLOBALS.DEV.baseURLJIRA;

      console.log('🎯 Support API URL:', {
        isProduction,
        baseURLJIRA,
        PROD_URL: GLOBALS.PROD.baseURLJIRA,
        DEV_URL: GLOBALS.DEV.baseURLJIRA
      });

      await axios.post(baseURLJIRA, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Success
      setShowModal(false);
      setSubmissionLoading(false);
      toast.success("Support ticket submitted successfully!", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      setFormData({
        subject: "",
        message: "",
      });

    } catch (err) {
      console.error('Error submitting support ticket:', err);
      setSubmissionLoading(false);

      // Handle different types of errors
      let errorMessage = 'Failed to submit support ticket. Please try again.';
      let errorType = 'general';

      if (err.code === 'ERR_NETWORK' || err.message.includes('ERR_CONNECTION_REFUSED')) {
        errorMessage = 'Unable to connect to the support system. Please check your internet connection and try again.';
        errorType = 'connection';
      } else if (err.code === 'ECONNREFUSED' || err.message.includes('ECONNREFUSED')) {
        errorMessage = 'The support system is currently unavailable. Please try again in a few moments or contact us directly at support@nutrad.io.';
        errorType = 'connection';
      } else if (err.response?.status === 404) {
        errorMessage = 'Support service not found. Please contact us directly at support@nutrad.io.';
        errorType = 'notfound';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Support system error occurred. Please try again later or contact us directly at support@nutrad.io.';
        errorType = 'server';
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        errorMessage = 'Access denied. Please log in again and try submitting your support ticket.';
        errorType = 'auth';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid support ticket data. Please check your information and try again.';
        errorType = 'validation';
      }

      setSubmissionError({ message: errorMessage, type: errorType, originalError: err });

      // Show toast error
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }, [submittedData, profile]);

  const handleCancel = () => {
    setShowModal(false);
    setSubmittedData(null);
    setSubmissionError(null);
    setSubmissionLoading(false);
  };

  const handleRetry = () => {
    setSubmissionError(null);
    handleConfirm();
  };

  // Show error if profile loading failed
  if (profileError) {
    return (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="min-h-screen bg-gradient-to-b from-sky-50 to-white py-12"
        >
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ translateY: 20, opacity: 0 }}
              animate={{ translateY: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center mb-12"
            >
              <div className="flex justify-center mb-6">
                <FaHeadset className="text-sky-500 text-6xl" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Contact nutrād Support
              </h1>
              <p className="text-xl text-gray-600">
                We're here to help! Please fill out this form for 24/7 support and general assistance.
              </p>
            </motion.div>

            <Alert variant="danger" className="d-flex align-items-center">
              <FaTriangleExclamation className="me-3" size={24} />
              <div className="flex-grow-1">
                <h5 className="alert-heading mb-2">Profile Loading Error</h5>
                <p className="mb-2">
                  Unable to load your profile information. This may be due to a connection issue or you may need to log in again.
                </p>
                <div className="small text-muted mb-3">
                  <FaWifi className="me-1" />
                  Please check your internet connection and try refreshing the page, or contact us directly at support@nutrad.io.
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => window.location.reload()}
                >
                  <MdRefresh className="me-1" />
                  Refresh Page
                </Button>
              </div>
            </Alert>
          </div>
        </motion.div>
        <Footer />
      </>
    );
  }

  // Show loading state
  if (profileLoading) {
    return (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="min-h-screen bg-gradient-to-b from-sky-50 to-white py-12"
        >
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ translateY: 20, opacity: 0 }}
              animate={{ translateY: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center mb-12"
            >
              <div className="flex justify-center mb-6">
                <FaHeadset className="text-sky-500 text-6xl" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Contact nutrād Support
              </h1>
              <p className="text-xl text-gray-600">
                We're here to help! Please fill out this form for 24/7 support and general assistance.
              </p>
            </motion.div>

            <div className="text-center py-12">
              <Spinner animation="border" role="status" className="mb-3" size="lg">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="text-gray-600">Loading your profile...</p>
            </div>
          </div>
        </motion.div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen bg-gradient-to-b from-sky-50 to-white py-12"
      >
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ translateY: 20, opacity: 0 }}
            animate={{ translateY: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center mb-12"
          >
            <div className="flex justify-center mb-6">
              <FaHeadset className="text-sky-500 text-6xl" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Contact nutrād Support
            </h1>
            <p className="text-xl text-gray-600">
              We're here to help! Please fill out this form for 24/7 support and general assistance.
            </p>
          </motion.div>

          <motion.div
            initial={{ translateY: 20, opacity: 0 }}
            animate={{ translateY: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white shadow-xl rounded-2xl p-8 mb-12"
          >
            <motion.form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div className="space-y-4">
                <div className="relative">
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition"
                    placeholder="Brief description of your issue"
                    required
                  />
                </div>
                <div className="relative">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg h-32 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition"
                    placeholder="Please describe your issue in detail"
                    required
                  />
                </div>
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-sky-500 text-white py-4 rounded-lg hover:bg-sky-600 transition duration-300 flex items-center justify-center space-x-2 text-lg font-medium"
              >
                <FaPaperPlane className="text-lg" />
                <span>Send Message</span>
              </motion.button>
            </motion.form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center text-gray-600"
          >
            <p className="flex items-center justify-center space-x-2">
              <FaEnvelope className="text-sky-500" />
              <span>You can also reach us directly at </span>
              <a href="mailto:support@nutrad.io" className="text-sky-500 hover:text-sky-600 font-medium">
                support@nutrad.io
              </a>
            </p>
          </motion.div>
        </div>
      </motion.div>

      <Modal
        show={showModal}
        onHide={handleCancel}
        centered
        className="rounded-lg"
      >
        <Modal.Header closeButton className="border-b-0 pb-0">
          <Modal.Title className="text-xl font-semibold text-gray-900">Confirm Support Ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-4">
          {submissionError ? (
            <Alert variant="danger" className="d-flex align-items-start mb-4">
              <FaTriangleExclamation className="me-3 mt-1" size={20} />
              <div className="flex-grow-1">
                <h6 className="alert-heading mb-2">Submission Error</h6>
                <p className="mb-2">{submissionError.message}</p>
                {submissionError.type === 'connection' && (
                  <div className="small text-muted mb-3">
                    <FaWifi className="me-1" />
                    This usually indicates a network connectivity issue or the support system is temporarily unavailable.
                  </div>
                )}
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleRetry}
                  disabled={submissionLoading}
                  className="me-2"
                >
                  {submissionLoading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <MdRefresh className="me-1" />
                      Try Again
                    </>
                  )}
                </Button>
              </div>
            </Alert>
          ) : (
            <p className="text-gray-600 mb-4">Are you sure you want to submit this support ticket?</p>
          )}

          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="mb-3">
              <strong className="text-gray-700">Subject:</strong>
              <p className="mt-1 text-gray-900">{submittedData?.subject}</p>
            </div>
            <div>
              <strong className="text-gray-700">Message:</strong>
              <p className="mt-1 text-gray-900">{submittedData?.message}</p>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-t-0 pt-0">
          <Button
            variant="outline-secondary"
            onClick={handleCancel}
            className="px-4 py-2"
            disabled={submissionLoading}
          >
            Cancel
          </Button>
          {!submissionError && (
            <Button
              variant="primary"
              onClick={handleConfirm}
              className="bg-sky-500 px-4 py-2"
              disabled={submissionLoading}
            >
              {submissionLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Footer />
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        limit={3}
      />
    </>
  );
}

export default Support;
